import React, { useState, useEffect } from "react";
import axios from "axios";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import {
  Grid,
  TextField,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  IconButton,
  Button,
} from "@mui/material";
import {
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainer,
} from "components/StyledComponents/styledComponents.js";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";
import Cookie from "js-cookie";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import ResponsiveTabs from "components/StyledComponents/styledComponents";

const ADMIN_ROLE_IDS = [1, 2];

export default function HRDocuments() {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [activeTab, setActiveTab] = useState(0);
  const [documents, setDocuments] = useState([]);

  const [documentName, setDocumentName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFileName, setPreviewFileName] = useState("");
  const [downloading, setDownloading] = useState(false);

  const [controller] = useMaterialUIController();
  const { user } = controller;
  const hrms_token = Cookie.get("hrms_token");
  const canManageDocuments = ADMIN_ROLE_IDS.includes(user?.roleId);

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchDocuments();
    }
  }, [activeTab]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(URLs.hrDocumentsUrl, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      setDocuments(data);
    } catch (error) {
      setAlert(`Failed to fetch documents: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDocumentName("");
    setDescription("");
    setSelectedFile(null);
  };

  const getFileNameFromUrl = (url) => {
    try {
      return decodeURIComponent(url.split("/").pop().split("?")[0]);
    } catch {
      return "document";
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setAlert("File size should be less than 10MB", "error");
      event.target.value = "";
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/png",
      "image/jpeg",
    ];
    if (!allowedTypes.includes(file.type)) {
      setAlert("Please upload a PDF, DOCX, PNG, or JPG/JPEG file", "error");
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  // Upload logic copied from useApplicant.js's handleFileChange (same bucket,
  // same presigned-url endpoint, same GET + PUT flow) - not reimplemented.
  const uploadFileToS3 = async (file) => {
    const uniqueFileName = `${user?.employeeId || "doc"}_${Date.now()}_${file.name}`;
    const apiUrl = `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(
      uniqueFileName
    )}&bucketName=sconexsofthrms&operation=upload`;
    const response = await axios.get(apiUrl, {
      headers: { Authorization: `Bearer ${hrms_token}` },
    });
    const url = response.data;
    if (!url || typeof url !== "string" || !url.startsWith("https://")) {
      throw new Error("Invalid pre-signed URL");
    }
    await axios.put(url, file, { headers: { "Content-Type": file.type } });
    return { s3FilePath: url.split("?")[0], fileName: uniqueFileName };
  };

  const handleUpload = async () => {
    if (!documentName.trim()) {
      setAlert("Document Name is required", "error");
      return;
    }
    if (!selectedFile) {
      setAlert("Please select a file to upload", "error");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setAlert("File size should be less than 10MB", "error");
      return;
    }

    setUploading(true);
    setLoading(true);
    try {
      const { s3FilePath, fileName } = await uploadFileToS3(selectedFile);

      const payload = {
        documentName,
        description,
        s3FilePath,
        fileName,
      };

      const response = await fetch(URLs.hrDocumentsUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Upload failed: ${response.status}`);
      }

      setAlert("Document uploaded successfully", "success");
      resetForm();
      setActiveTab(0);
      fetchDocuments();
    } catch (error) {
      console.error("Upload error:", error);
      setAlert(`Upload failed: ${error.message}`, "error");
    } finally {
      setUploading(false);
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${URLs.hrDocumentsUrl}/${documentId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.status}`);
      }

      setAlert("Document deleted successfully", "success");
      fetchDocuments();
    } catch (error) {
      setAlert(`Failed to delete document: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Preview / download logic copied from Applicant.js's handleResumeView /
  // handleDownloadResume (same presigned-url pattern, operation=download).
  const getDownloadUrl = async (doc) => {
    const fileName = doc.fileName || getFileNameFromUrl(doc.s3FilePath);
    const apiUrl = `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(
      fileName
    )}&bucketName=sconexsofthrms&operation=download`;

    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${hrms_token}`,
      },
    });

    const url = response.data;
    if (!url || typeof url !== "string" || !url.startsWith("https://")) {
      throw new Error("Invalid pre-signed URL");
    }
    return { url, fileName };
  };

  const handleViewDocument = async (doc) => {
    setLoading(true);
    try {
      const { url, fileName } = await getDownloadUrl(doc);
      setPreviewUrl(url);
      setPreviewFileName(fileName);
      setPreviewOpen(true);
    } catch (err) {
      setAlert(`Failed to load document: ${err.message || "Unknown error"}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    setDownloading(true);
    try {
      const { url, fileName } = await getDownloadUrl(doc);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setAlert("Failed to download document", "error");
    } finally {
      setDownloading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl(null);
    setPreviewFileName("");
  };

  const isImageFile = (fileName) => /\.(png|jpe?g)$/i.test(fileName || "");
  const isPdfFile = (fileName) => /\.pdf$/i.test(fileName || "");

  const tabs = [{ label: "Document List" }, { label: "Upload Document" }];

  const columns = [
    {
      headerName: "Document Name",
      field: "documentName",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Description",
      field: "description",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Created Date",
      valueGetter: (params) => params.data?.createdTimestamp || "",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Actions",
      field: "actions",
      sortable: false,
      filter: false,
      width: 150,
      cellRenderer: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleViewDocument(params.data)} color="primary">
            <VisibilityIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDownloadDocument(params.data)}
            color="primary"
          >
            <DownloadIcon />
          </IconButton>
          {canManageDocuments && (
            <IconButton
              size="small"
              onClick={() => handleDeleteDocument(params.data.id)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", mt: 0 }}>
        <ResponsiveTabs
          tabs={canManageDocuments ? tabs : [tabs[0]]}
          activeTab={canManageDocuments ? activeTab : 0}
          onChange={(_, newValue) => setActiveTab(newValue)}
          labelColor="secondary"
          bgColor="#e0f7fa"
        />

        <StyledSnackbar
          open={open}
          autoHideDuration={5000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <StyledAlert onClose={handleClose} severity={alertType}>
            {message}
          </StyledAlert>
        </StyledSnackbar>

        {activeTab === 0 ? (
          <Box sx={{ mt: 2 }}>
            <StyledAgGridContainer
              component={Paper}
              className="ag-theme-alpine"
              sx={{
                flexGrow: 1,
                backgroundColor: "transparent",
                height: "calc(100vh - 200px)",
                width: "100%",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <AgGridReact
                  rowData={documents}
                  columnDefs={columns}
                  pagination={true}
                  paginationPageSize={9}
                  defaultColDef={{
                    resizable: true,
                    filter: true,
                  }}
                  overlayNoRowsTemplate="<span>No documents found</span>"
                />
              )}
            </StyledAgGridContainer>
          </Box>
        ) : (
          canManageDocuments && (
            <Box sx={{ mt: 2, width: "100%" }}>
              <Paper
                sx={{
                  display: "flex",
                  backgroundColor: "#ffffff",
                  minHeight: "300px",
                  borderRadius: "15px",
                  overflow: "auto",
                  width: "100%",
                  p: 3,
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Document Name"
                      value={documentName}
                      onChange={(e) => setDocumentName(e.target.value)}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Button variant="outlined" component="label" fullWidth>
                      {selectedFile ? selectedFile.name : "Upload File *"}
                      <input type="file" hidden onChange={handleFileSelect} />
                    </Button>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Supported formats: PDF, DOCX, JPG, PNG
                      <br />
                      Maximum file size: 10 MB
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                      <StyledButton onClick={handleUpload} disabled={uploading || loading}>
                        {uploading ? "Uploading..." : "Upload Document"}
                      </StyledButton>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )
        )}

        <Dialog open={previewOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
          <DialogContent>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "400px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : isPdfFile(previewFileName) ? (
              <object
                data={previewUrl}
                type="application/pdf"
                style={{ width: "100%", height: "80vh" }}
              >
                <p>
                  Unable to display PDF. Please download to view: <a href={previewUrl}>Download</a>
                </p>
              </object>
            ) : isImageFile(previewFileName) ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 2,
                }}
              >
                <img
                  src={previewUrl}
                  alt="Document"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "400px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body1" align="center">
                Unable to display this file type. Please download to view.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <StyledButton
              onClick={() =>
                handleDownloadDocument({ s3FilePath: previewUrl, fileName: previewFileName })
              }
              disabled={downloading || loading}
            >
              {downloading ? "Downloading..." : "Download"}
            </StyledButton>
            <StyledButton onClick={handleClosePreview}>Close</StyledButton>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}
